import React, { useState, useCallback } from 'react';

const SportsGameCard = React.memo(({ game, favorites, handleGameClick, addToFavorites }) => {
  const [imageError, setImageError] = useState(false);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const isFavorite = favorites.includes(game.id);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const getBestThumbnail = useCallback(() => {
    const thumbnails = [game.thumbnail, ...Object.values(game.thumbnails.extra || [])];
    return thumbnails.find(thumb => thumb && !imageError) || game.thumbnail;
  }, [game.thumbnail, game.thumbnails.extra, imageError]);

  const handleClick = useCallback(() => {
    if (!isLongPressing) {
      handleGameClick(game);
    }
  }, [game, isLongPressing, handleGameClick]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsLongPressing(false);

    const target = e.currentTarget;

    const longPressTimer = setTimeout(() => {
      setIsLongPressing(true);
      addToFavorites(game.id);

      if (navigator.vibrate) {
        navigator.vibrate(100);
      }

      if (target && target.style) {
        target.style.transform = 'scale(0.95)';
        setTimeout(() => {
          if (target && target.style) {
            target.style.transform = '';
          }
        }, 150);
      }

    }, 500);

    const handleTouchEnd = () => {
      clearTimeout(longPressTimer);
      setTimeout(() => setIsLongPressing(false), 100);
    };

    const handleTouchMove = () => {
      clearTimeout(longPressTimer);
      setIsLongPressing(false);
    };

    document.addEventListener('touchend', handleTouchEnd, { once: true });
    document.addEventListener('touchmove', handleTouchMove, { once: true });
  }, [game.id, addToFavorites]);

  return (
    <div
      className="bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer overflow-hidden group relative"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
    >
      {isFavorite && (
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white z-10 rounded-full p-1 sm:p-1.5 shadow-lg">
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
          </svg>
        </div>
      )}

      <div className="relative overflow-hidden">
        {!imageError ? (
          <img
            src={getBestThumbnail()}
            alt={game.title}
            className="w-full h-36 sm:h-40 md:h-48 object-cover transition-transform duration-500 group-hover:scale-110"
            onError={handleImageError}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-36 sm:h-40 md:h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <div className="text-white text-4xl sm:text-5xl md:text-6xl">🎮</div>
          </div>
        )}

        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
          <div className="bg-white bg-opacity-95 rounded-full p-3 sm:p-4 md:p-5 transform scale-0 group-hover:scale-100 transition-transform duration-300 shadow-lg">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-2 sm:p-3 md:p-4">
          <h3 className="text-white font-bold text-sm sm:text-base md:text-lg mb-1 line-clamp-2 sm:line-clamp-1 leading-tight">
            {game.title}
          </h3>
          <span className="text-blue-300 text-xs sm:text-sm font-medium">
            {game.genre}
          </span>
        </div>

        {new Date(game.releaseDate) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-green-500 text-white text-xs px-2 py-1 sm:px-3 sm:py-1 rounded-full font-bold shadow-lg">
            NEW
          </div>
        )}
      </div>
    </div>
  );
});

export default SportsGameCard;
